
import { format, isBefore, startOfTomorrow, addHours } from "date-fns";
import CalendarHeader from "./CalendarHeader";
import CalendarDay from "./CalendarDay";
import { useAdminBypass } from "@/contexts/AdminBypassContext";

interface SingleCalendarProps {
  displayDate: Date;
  selectedDate: Date | null;
  onDaySelect: (day: number) => void;
  onNavigate: (direction: 'prev' | 'next') => void;
  isStartCalendar: boolean;
  startDate: Date | null;
}

const SingleCalendar = ({
  displayDate,
  selectedDate,
  onDaySelect,
  onNavigate,
  isStartCalendar,
  startDate,
}: SingleCalendarProps) => {
  const { canBypassDateRestrictions } = useAdminBypass();
  
  // Admin can select from now, others need 48 hours ahead
  const minDate = canBypassDateRestrictions ? new Date() : addHours(new Date(), 48);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const daysInMonth = new Date(
    displayDate.getFullYear(),
    displayDate.getMonth() + 1,
    0
  ).getDate();
  
  const firstDayOfMonth = new Date(
    displayDate.getFullYear(),
    displayDate.getMonth(),
    1
  ).getDay();

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDayOfMonth }, (_, i) => i);

  const isDaySelected = (day: number) => {
    if (!selectedDate) return false;
    
    // Create a date object for the day we're rendering
    const currentDate = new Date(
      displayDate.getFullYear(), 
      displayDate.getMonth(), 
      day,
      // Use appropriate time based on whether it's start or end date
      isStartCalendar ? 12 : 23,
      isStartCalendar ? 0 : 59,
      isStartCalendar ? 0 : 59
    );
    
    // Compare year, month, and day only (ignoring time)
    return (
      currentDate.getFullYear() === selectedDate.getFullYear() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getDate() === selectedDate.getDate()
    );
  };

  const isDateDisabled = (day: number) => {
    const currentDate = new Date(displayDate.getFullYear(), displayDate.getMonth(), day, 12, 0, 0);
    // Check if date is before the 48-hour mark
    if (isBefore(currentDate, minDate)) return true;
    // If this is the end calendar, also check if it's before the start date
    if (!isStartCalendar && startDate && isBefore(currentDate, startDate)) return true;
    return false;
  };


  return (
    <div className="space-y-4">
      <CalendarHeader displayDate={displayDate} onNavigate={onNavigate} />

      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-xs font-medium text-muted-foreground text-center py-1"
          >
            {day}
          </div>
        ))}
        {emptyDays.map((_, index) => (
          <div key={`empty-${index}`} className="h-8" />
        ))}
        {days.map((day) => (
          <CalendarDay
            key={day}
            day={day}
            isSelected={isDaySelected(day)}
            isDisabled={isDateDisabled(day)}
            onSelect={onDaySelect}
          />
        ))}
      </div>

      {selectedDate && (
        <div className="text-sm text-muted-foreground text-center">
          Selected: {format(selectedDate, 'MMM d')}
        </div>
      )}
    </div>
  );
};

export default SingleCalendar;
